import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Send, Trash2, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/keeper/letters`;
const SEASONS = ["spring", "summer", "autumn", "winter"];
const field = "mt-1 w-full bg-transparent border border-[#303B2D] rounded-sm px-3 py-2.5 text-[14px] text-[#E8DDC7] focus:outline-none focus:border-[#B5A642]/70 transition-colors";
const btn = "inline-flex items-center gap-2 px-4 py-2 border border-[#B5A642]/60 text-[11px] uppercase tracking-[0.2em] text-[#D3CEC4] rounded-sm hover:bg-[#B5A642]/10 transition-colors disabled:opacity-40";

const Editor = ({ letter, auth, audience, emailReady, onDone }) => {
  const [f, setF] = useState({ en: letter.en, fr: letter.fr });
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const set = (lang, k) => (e) => setF((x) => ({ ...x, [lang]: { ...x[lang], [k]: e.target.value } }));
  const run = async (kind, fn) => {
    setBusy(kind); setMsg("");
    try { await fn(); onDone(); } catch (e) { setMsg(e.response?.data?.detail || "That didn't work. Please try again."); } finally { setBusy(""); }
  };
  const save = () => run("save", () => axios.put(`${API}/${letter.letter_id}`, f, auth));
  const send = () => {
    if (!window.confirm(`Send this letter to ${audience} people now? This can't be undone.`)) return;
    run("send", async () => {
      await axios.put(`${API}/${letter.letter_id}`, f, auth);
      await axios.post(`${API}/${letter.letter_id}/send`, { origin_url: window.location.origin }, auth);
    });
  };
  const discard = () => window.confirm("Discard this draft?") && run("discard", () => axios.delete(`${API}/${letter.letter_id}`, auth));
  return (
    <div className="mt-4 grid lg:grid-cols-2 gap-6">
      {["en", "fr"].map((lang) => (
        <div key={lang}>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#B5A642]/80">{lang === "en" ? "English" : "Français"}</p>
          <input className={field} value={f[lang].subject} onChange={set(lang, "subject")} data-testid={`letter-${lang}-subject`} />
          <textarea rows={14} className={`${field} leading-relaxed`} value={f[lang].body} onChange={set(lang, "body")} data-testid={`letter-${lang}-body`} />
        </div>
      ))}
      <div className="lg:col-span-2 flex flex-wrap items-center gap-3">
        <button className={btn} disabled={!!busy} onClick={save} data-testid="letter-save">{busy === "save" ? "Saving…" : "Save changes"}</button>
        <button className={`${btn} bg-[#4B3226] border-[#4B3226] text-[#E8DDC7]`} disabled={!!busy || !emailReady || !audience} onClick={send} data-testid="letter-send">
          <Send className="w-3.5 h-3.5" /> {busy === "send" ? "Sending…" : `Approve and send to ${audience}`}
        </button>
        <button className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#D3CEC4]/55 hover:text-[#C9876B] transition-colors" disabled={!!busy} onClick={discard} data-testid="letter-discard">
          <Trash2 className="w-3.5 h-3.5" /> Discard
        </button>
        {!emailReady && <span className="text-[12px] text-[#C9A96B]" data-testid="letter-email-off">Email isn't switched on yet, so sending is disabled.</span>}
        {msg && <span className="text-[12px] text-[#C9876B]" data-testid="letter-error">{msg}</span>}
      </div>
    </div>
  );
};

export const KeeperLetters = ({ token }) => {
  const [d, setD] = useState(null);
  const [season, setSeason] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => {
    axios.get(API, { headers: { Authorization: `Bearer ${token}` } }).then((r) => { setD(r.data); setSeason((s) => s || r.data.season); }).catch(() => setD({ letters: [] }));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const draft = async () => {
    setDrafting(true); setError("");
    try {
      const r = await axios.post(`${API}/draft`, { season }, auth);
      setOpen(r.data.letter_id); load();
    } catch (e) { setError(e.response?.data?.detail || "The writer couldn't finish. Please try again."); } finally { setDrafting(false); }
  };

  if (!d) return <p className="mt-8 text-[#D3CEC4]/50">Fetching the letter box…</p>;
  const letters = Array.isArray(d.letters) ? d.letters : [];
  return (
    <div className="mt-6" data-testid="keeper-letters">
      <p className="text-[14px] text-[#D3CEC4]/70" data-testid="letters-audience">
        Goes to {d.audience ?? 0} people: email subscribers plus customers who ordered in the last two years, minus anyone who unsubscribed.
      </p>
      <p className="mt-1 text-[12px] text-[#D3CEC4]/50">A draft is written for you automatically when each season begins. Nothing is sent until you approve it.</p>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <select value={season} onChange={(e) => setSeason(e.target.value)} data-testid="letters-season"
          className="bg-[#1C1F1E] border border-[#303B2D] rounded-sm px-3 py-2 text-[13px] text-[#E8DDC7] capitalize">
          {SEASONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className={btn} onClick={draft} disabled={drafting} data-testid="letters-draft">
          {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} {drafting ? "Writing… about 30 seconds" : "Draft a letter"}
        </button>
        {error && <span className="text-[12px] text-[#C9876B]">{error}</span>}
      </div>
      {letters.length === 0 ? <p data-testid="letters-empty" className="mt-8 font-display italic text-[#D3CEC4]/55">No letters yet.</p> : (
        <ul className="mt-6 space-y-4">
          {letters.map((l) => (
            <li key={l.letter_id} className="border border-[#303B2D]/70 rounded-sm p-5" data-testid={`letter-${l.letter_id}`}>
              <button onClick={() => setOpen(open === l.letter_id ? null : l.letter_id)} className="w-full flex flex-wrap justify-between gap-2 text-left" data-testid={`letter-open-${l.letter_id}`}>
                <span className="font-display text-xl text-[#E8DDC7]">{l.en?.subject || "Draft failed"}</span>
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#D3CEC4]/55">
                  <span className="capitalize">{l.season} {l.year}</span> · <span data-testid={`letter-status-${l.letter_id}`}>{l.status}</span>
                  {l.status === "sent" && ` · ${l.sent_count ?? 0} sent`}{l.drafted_by === "auto" && " · auto-drafted"}
                </span>
              </button>
              {open === l.letter_id && l.status === "draft" && (
                <Editor letter={l} auth={auth} audience={d.audience ?? 0} emailReady={!!d.email_ready} onDone={() => { setOpen(null); load(); }} />
              )}
              {open === l.letter_id && l.status !== "draft" && l.en && (
                <div className="mt-4 whitespace-pre-line text-[14px] leading-relaxed text-[#D3CEC4]/75">{l.en.body}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
